import React from 'react';
import { Button, Flex } from 'antd';
import CustomImage from './CustomImage';
import type { SizeType } from 'antd/es/config-provider/SizeContext';
import { Property } from 'csstype';
import { legacyBorder, legacyButtonColors } from '@/utils/legacyColor';

interface CustomButtonProps {
  loader?: React.ReactNode;
  text?: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
  width?: string;
  minWidth?: string;
  block?: boolean;
  height?: number | string;
  bgColor?: string;
  color?: string;
  fontSize?: Property.FontSize<string>;
  fontWeight?: Property.FontWeight;
  lineHeight?: string;
  borderRadius?: string;
  boxShadow?: string;
  iconPosition?: 'left' | 'right';
  imageWidth?: string;
  icon?: React.ReactNode;
  paddingImage?: string;
  image?: string;
  marginTop?: string;
  marginBottom?: string;
  border?: string;
  gap?: React.CSSProperties['gap'] | SizeType;
  padding?: string;
  className?: string;
  isFormValid?: boolean;
  /** When true, button is disabled (same visual as isFormValid). */
  disabled?: boolean;
  htmlType?: 'button' | 'submit' | 'reset';
  disableBgColor?: string;
}

const CustomButton: React.FC<CustomButtonProps> = ({
  loader,
  text,
  onClick,
  width,
  minWidth,
  block,
  height,
  bgColor,
  color,
  fontSize,
  fontWeight,
  lineHeight,
  borderRadius,
  boxShadow,
  iconPosition,
  imageWidth,
  icon,
  paddingImage,
  image,
  marginTop,
  marginBottom,
  border,
  gap,
  padding,
  className,
  isFormValid = false,
  disabled = false,
  htmlType = 'button',
  disableBgColor = 'var(--zl-btn-disabled-bg, #F5F5F5)',
}) => {
  const isDisabled = Boolean(disabled) || Boolean(isFormValid);
  const colors = legacyButtonColors(bgColor, color);
  const buttonClassName =
    [className, colors.ink && !isDisabled && 'zl-btn-ink']
      .filter(Boolean)
      .join(' ') || undefined;
  return (
    <Button
      type='primary'
      disabled={isDisabled}
      htmlType={htmlType}
      onClick={onClick}
      block={block}
      className={buttonClassName}
      style={{
        height: height,
        backgroundColor: isDisabled ? disableBgColor : colors.bgColor,
        color: isDisabled
          ? 'var(--zl-btn-disabled-text, #AFAFAF)'
          : colors.color,
        fontSize: fontSize,
        width: width,
        minWidth: minWidth,
        borderRadius: borderRadius,
        boxShadow: boxShadow,
        fontWeight: fontWeight,
        lineHeight: lineHeight,
        border: legacyBorder(border) || 'none',
        padding: padding,
        marginTop: marginTop,
        marginBottom: marginBottom,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
      }}
    >
      <Flex gap={gap} align='center'>
        {loader && <span className='px-1'>{loader}</span>}
        {/* Render icon or image on the left */}
        {iconPosition === 'left' &&
          (image ? (
            <CustomImage
              width={imageWidth}
              src={image}
              preview={false}
              style={{ opacity: isDisabled ? 0.3 : 1, padding: paddingImage }}
            />
          ) : (
            icon
          ))}

        {/* Button text */}
        {text}

        {/* Render icon or image on the right */}
        {iconPosition === 'right' &&
          (image ? (
            <CustomImage
              width={imageWidth}
              src={image}
              preview={false}
              style={{ opacity: isDisabled ? 0.3 : 1, padding: paddingImage }}
            />
          ) : (
            icon
          ))}
      </Flex>
    </Button>
  );
};

export default CustomButton;
